import { type DataEditorProps, GridCellKind, type GridCell, type Item, type Rectangle } from "@artis/data-grid";
import { useCallback, useEffect, useRef, useState } from "react";

import { shiftRefs } from "../formula/shift";
import type { EditSession } from "./useEditSession";

type ClipboardDeps = {
  activeSheetId: string;
  getSource: (cell: Item) => string;
  getDisplay: (cell: Item) => string;
  setValue: (cell: Item, raw: string) => void;
  moveRange: (rect: Rectangle, target: Item) => void;
  transact: (fn: () => void) => void;
};

type InternalClipboard = {
  mode: "copy" | "cut";
  sheetId: string;
  origin: Item;
  size: { width: number; height: number };
  sources: string[][];
  display: string[][];
};

export type ClipRegion = { mode: "copy" | "cut"; sheetId: string; range: Rectangle };

export function useClipboard(session: EditSession, deps: ClipboardDeps) {
  const { gridWrapRef, selectionRef, draftRef } = session;
  const depsRef = useRef(deps);
  depsRef.current = deps;
  const clipRef = useRef<InternalClipboard | null>(null);
  const cutArmedRef = useRef(false);
  const [clipRegion, setClipRegion] = useState<ClipRegion | null>(null);

  useEffect(() => {
    const el = gridWrapRef.current;
    if (!el) return;
    const snapshot = (mode: "copy" | "cut") => {
      const range = selectionRef.current?.current?.range;
      if (!range) {
        clipRef.current = null;
        setClipRegion(null);
        return;
      }
      const { getSource, getDisplay } = depsRef.current;
      const sources: string[][] = [];
      const display: string[][] = [];
      for (let r = 0; r < range.height; r++) {
        const sourceRow: string[] = [];
        const displayRow: string[] = [];
        for (let c = 0; c < range.width; c++) {
          const cell: Item = [range.x + c, range.y + r];
          sourceRow.push(getSource(cell));
          displayRow.push(getDisplay(cell));
        }
        sources.push(sourceRow);
        display.push(displayRow);
      }
      clipRef.current = {
        mode,
        sheetId: depsRef.current.activeSheetId,
        origin: [range.x, range.y],
        size: { width: range.width, height: range.height },
        sources,
        display,
      };
      setClipRegion({ mode, sheetId: depsRef.current.activeSheetId, range });
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (draftRef.current) return;
      if ((e.ctrlKey || e.metaKey) && e.key === "c") snapshot("copy");
      if ((e.ctrlKey || e.metaKey) && e.key === "x") {
        snapshot("cut");
        cutArmedRef.current = clipRef.current !== null;
      }
      if (e.key === "Escape" && clipRef.current) {
        clipRef.current = null;
        setClipRegion(null);
      }
    };
    el.addEventListener("keydown", onKeyDown, { capture: true });
    return () => el.removeEventListener("keydown", onKeyDown, { capture: true });
  }, [gridWrapRef, selectionRef, draftRef]);

  const getCellsForSelection = useCallback((rect: Rectangle) => {
    const { getSource, getDisplay } = depsRef.current;
    const rows: GridCell[][] = [];
    for (let r = 0; r < rect.height; r++) {
      const row: GridCell[] = [];
      for (let c = 0; c < rect.width; c++) {
        const cell: Item = [rect.x + c, rect.y + r];
        const display = getDisplay(cell);
        row.push({
          kind: GridCellKind.Text,
          data: getSource(cell),
          displayData: display,
          copyData: display,
          allowOverlay: true,
        });
      }
      rows.push(row);
    }
    return rows;
  }, []) satisfies DataEditorProps["getCellsForSelection"];

  const onDelete = useCallback(() => {
    if (cutArmedRef.current) {
      cutArmedRef.current = false;
      return false;
    }
    return true;
  }, []) satisfies DataEditorProps["onDelete"];

  const onPaste = useCallback((target: Item, values: readonly (readonly string[])[]) => {
    const { setValue } = depsRef.current;
    const clip = clipRef.current;
    const internal =
      clip !== null &&
      clip.display.length === values.length &&
      clip.display.every((row, r) => row.length === values[r]!.length && row.every((v, c) => v === values[r]![c]));
    if (internal && clip.mode === "cut" && clip.sheetId === depsRef.current.activeSheetId) {
      depsRef.current.moveRange(
        { x: clip.origin[0], y: clip.origin[1], width: clip.size.width, height: clip.size.height },
        target,
      );
      clipRef.current = null;
      setClipRegion(null);
      return false;
    }
    depsRef.current.transact(() => {
      for (let r = 0; r < values.length; r++) {
        for (let c = 0; c < values[r]!.length; c++) {
          const dest: Item = [target[0] + c, target[1] + r];
          const text = internal
            ? shiftRefs(clip.sources[r]![c]!, target[0] - clip.origin[0], target[1] - clip.origin[1])
            : values[r]![c]!;
          setValue(dest, text);
        }
      }
    });
    return false;
  }, []);

  return { clipRegion, getCellsForSelection, onDelete, onPaste };
}
